import React, { useEffect, useState } from 'react'
import ReactModal from 'react-modal'
import { ReactComponent as CloseModalIcon } from '../../assets/svg/CloseModalIcon.svg'
import { ReactComponent as Calendar } from '../../assets/svg/Calendar.svg'
import { useTranslation } from 'react-i18next'
import { create, sendNotification } from '../../server/server'
import { format_day_only, notify, translateTargetText } from '../../utils/util'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import deLocale from 'date-fns/locale/de'
import enLocale from 'date-fns/locale/en-US'
import arLocale from 'date-fns/locale/ar-SA'
import TextField from '@mui/material/TextField'
import i18n from '../../i18n'
import { DAYS } from '../../constants/Constants'
import PickTimeModule from './PickTimeModule'
import TargetGroupModal from './TargetGroupModal'
import { useUser } from '../../auth'
import Loading from '../Loading'

const localeMap = { ar: arLocale, en: enLocale, de: deLocale }

/**
 * Re-runs an existing survey: creates a new `content` run from a previous one,
 * keeping the same survey (and target group by default) but with a new start
 * date. Optionally repeats it weekly. The backend supersedes the previous live
 * run automatically (one live run per survey_id).
 */
function RerunSurveyModal({ modalIsOpen, closeModal, content, onDone }) {
    const { t } = useTranslation()
    const { user } = useUser()

    const [startDate, setStartDate] = useState(new Date())
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)
    const [returnHour, setReturnHour] = useState(8)
    const [returnMinute, setReturnMinute] = useState(0)

    const [target, setTarget] = useState({ name: content?.target_text || 'Select target', ids: content?.target || [] })
    const [showTarget, setShowTarget] = useState(false)

    const [recurrent, setRecurrent] = useState(false)
    const [recurDays, setRecurDays] = useState([])
    const [recurEndDate, setRecurEndDate] = useState(null)
    const [isEndPickerOpen, setIsEndPickerOpen] = useState(false)

    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState('')

    useEffect(() => {
        if (modalIsOpen) {
            ReactModal.setAppElement('#root')
            // Reset to the source run's target each time the modal opens.
            setTarget({ name: content?.target_text || 'Select target', ids: content?.target || [] })
            setRecurrent(false)
            setRecurDays([])
            setRecurEndDate(null)
            setError('')
        }
    }, [modalIsOpen, content])

    const setReturnTarget = (data) => {
        setTarget({ name: data?.name || 'Select target', ids: (data?.target || []).map((a) => a?.id ?? a) })
    }

    const toggleDay = (value) => {
        setError('')
        setRecurDays((prev) => (prev.includes(value) ? prev.filter((d) => d !== value) : [...prev, value]))
    }

    const buildScheduleDate = () => {
        const date = new Date(startDate)
        date.setHours(returnHour)
        date.setMinutes(returnMinute)
        date.setSeconds(0)
        date.setMilliseconds(0)
        return date.getTime()
    }

    const validate = () => {
        if (!target.ids?.length) {
            setError(t('Please select a target group'))
            return false
        }
        if (recurrent && recurDays.length === 0) {
            setError(t('selectDays'))
            return false
        }
        if (recurrent && !recurEndDate) {
            setError(t('Please select an end date'))
            return false
        }
        return true
    }

    const handleRerun = async () => {
        if (!validate()) return
        setSubmitting(true)
        const scheduleDate = buildScheduleDate()
        const data = {
            title: content?.title,
            description: content?.description,
            sections: content?.sections || [],
            preCreated: content?.preCreated,
            survey_id: content?.survey_id, // template link → groups runs together
            type: 'survey',
            institution_id: user.institution_id,
            device: 'desktop',
            target: target.ids,
            target_text: target.name,
            user_id: user.id,
            status: 'start',
            anonymous: !!content?.anonymous,
            required: !!content?.required,
            hash: Date.now() + user.id.toString(36),
            created_date: Date.now(),
            schedule_date: scheduleDate,
            schedule: scheduleDate > Date.now(),
            publicationDate: scheduleDate,
            created_by: { first_name: user.first_name, last_name: user.last_name },
        }
        if (recurrent) {
            data.repeat = true
            data.repeatData = {
                repeatType: 'weekly',
                selectedDays: recurDays,
                endOfRecurrenceDate: new Date(recurEndDate).getTime(),
            }
        }
        const response = await create({ collection: 'content', data })
        setSubmitting(false)
        if (response.status === 200) {
            notify(t('surveyIsCreated'))
            sendNotification({ content: response.data, type: 'survey' })
            onDone && onDone(response.data)
            closeModal()
        } else {
            setError(t('System error'))
        }
    }

    return (
        <ReactModal
            transparent
            isOpen={modalIsOpen}
            ariaHideApp={false}
            onRequestClose={closeModal}
            style={{
                overlay: { zIndex: 30, overflowY: 'auto', height: '100vh', backgroundColor: 'rgba(44,44,44,0.5)' },
                content: {
                    inset: 0,
                    padding: 0,
                    margin: 0,
                    backgroundColor: 'transparent',
                    border: 'none',
                    boxShadow: 'none',
                    overflow: 'hidden',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                },
            }}
        >
            <div className="flex flex-col items-center w-full">
                <div
                    className="w-[60px] h-[60px] rounded-full cursor-pointer bg-deepPurple hover:bg-darkenPurple flex items-center justify-center z-[2] mb-3"
                    onClick={() => closeModal()}
                >
                    <CloseModalIcon />
                </div>

                <div className="bg-[#F8F8FB] w-[600px] rounded-[10px] overflow-hidden">
                    <div className="pt-[40px] pb-[28px] bg-[#ECEBF8] flex flex-col gap-[8px] items-center">
                        <p className="text-[28px] text-[#5E639B] leading-[34px] font-bold">{t('Rerun Survey')}</p>
                        <p className="text-[16px] text-mudPurple leading-[20px] font-medium">{content?.title}</p>
                    </div>

                    <div className="flex flex-col pt-[26px] pb-[34px] px-[44px] gap-5">
                        <p className="text-[14px] text-[#9392AD] font-medium leading-[20px] text-center">
                            {t('Run this survey again with a new start date for the same target group')}.
                        </p>

                        {/* Target group */}
                        <div className="flex flex-row items-center justify-between">
                            <p className="text-base font-semibold text-mudPurple">{t('targetGroup')}:</p>
                            <div
                                className="bg-[#ECEBF8] rounded-full px-5 h-[44px] flex items-center cursor-pointer text-deepPurple font-semibold"
                                onClick={() => setShowTarget(true)}
                            >
                                {translateTargetText(target.name)}
                            </div>
                        </div>

                        {/* Start date */}
                        <div className="flex flex-row items-center justify-between">
                            <p className="text-base font-semibold text-mudPurple">{t('startDate')}:</p>
                            <div
                                className="bg-[#ECEBF8] rounded-full px-5 h-[44px] flex items-center gap-2 cursor-pointer text-mudPurple font-semibold"
                                onClick={() => setIsDatePickerOpen(true)}
                            >
                                <Calendar style={{ color: '#9392AD' }} />
                                <p>{format_day_only(startDate)}</p>
                            </div>
                            <div style={{ position: 'absolute', visibility: 'hidden' }}>
                                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={localeMap[i18n.language]}>
                                    <DatePicker
                                        value={startDate}
                                        onChange={(date) => date && setStartDate(date)}
                                        open={isDatePickerOpen}
                                        onClose={() => setIsDatePickerOpen(false)}
                                        minDate={new Date()}
                                        renderInput={(params) => <TextField {...params} inputProps={{ ...params.inputProps, readOnly: true }} />}
                                        inputFormat={'dd/MM/yyyy'}
                                        disableMaskedInput={true}
                                    />
                                </LocalizationProvider>
                            </div>
                        </div>

                        {/* Time */}
                        <div className="flex flex-row items-center justify-between">
                            <p className="text-base font-semibold text-mudPurple">{t('setATime')}:</p>
                            <PickTimeModule hour={returnHour} minute={returnMinute} setReturnMinute={setReturnMinute} setReturnHour={setReturnHour} />
                        </div>

                        {/* Recurrence */}
                        <div className="flex flex-row items-center justify-between">
                            <p className="text-base font-semibold text-mudPurple">{t('Repeat weekly')}:</p>
                            <div
                                className={`px-5 h-[40px] flex items-center rounded-full cursor-pointer font-semibold no-text-select ${
                                    recurrent ? 'bg-deepPurple text-white' : 'bg-[#ECEBF8] text-deepPurple'
                                }`}
                                onClick={() => setRecurrent((prev) => !prev)}
                            >
                                {recurrent ? t('yes') : t('no')}
                            </div>
                        </div>

                        {recurrent && (
                            <div className="flex flex-col gap-3">
                                <div className="bg-lightBG rounded-full flex items-center justify-between px-4 h-[44px]">
                                    <p className="font-semibold text-[#9392AD]">{t('selectDays')}:</p>
                                    <div className="flex flex-row items-center">
                                        {DAYS.map((day) => {
                                            const selected = recurDays.includes(day.value)
                                            return (
                                                <div
                                                    key={day.value}
                                                    className={`mx-[2px] pointer py-[5px] px-[7px] text-[15px] font-semibold transition-all ${
                                                        selected ? 'text-[#5E639B] bg-white rounded-full' : 'text-[#BEBECE]'
                                                    }`}
                                                    onClick={() => toggleDay(day.value)}
                                                >
                                                    {t(day.name)}
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                                <div className="flex flex-row items-center justify-between">
                                    <p className="text-base font-semibold text-mudPurple">{t('Ends on')}:</p>
                                    <div
                                        className="bg-[#ECEBF8] rounded-full px-5 h-[44px] flex items-center gap-2 cursor-pointer text-mudPurple font-semibold"
                                        onClick={() => setIsEndPickerOpen(true)}
                                    >
                                        <Calendar style={{ color: '#9392AD' }} />
                                        <p>{recurEndDate ? format_day_only(recurEndDate) : t('Select date')}</p>
                                    </div>
                                    <div style={{ position: 'absolute', visibility: 'hidden' }}>
                                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={localeMap[i18n.language]}>
                                            <DatePicker
                                                value={recurEndDate}
                                                onChange={(date) => setRecurEndDate(date)}
                                                open={isEndPickerOpen}
                                                onClose={() => setIsEndPickerOpen(false)}
                                                minDate={startDate}
                                                renderInput={(params) => <TextField {...params} inputProps={{ ...params.inputProps, readOnly: true }} />}
                                                inputFormat={'dd/MM/yyyy'}
                                                disableMaskedInput={true}
                                            />
                                        </LocalizationProvider>
                                    </div>
                                </div>
                            </div>
                        )}

                        {!!error && <p className="text-deleteRed text-sm font-semibold text-center">{error}</p>}

                        <button
                            className="bg-deepPurple hover:bg-highlightColor rounded-full py-3 px-7 text-highlightColor hover:text-deepPurple font-semibold text-lg flex items-center justify-center disabled:opacity-60"
                            onClick={handleRerun}
                            disabled={submitting}
                        >
                            {submitting ? <Loading size={22} /> : t('Rerun Survey')}
                        </button>
                    </div>
                </div>
            </div>

            <TargetGroupModal showTarget={showTarget} closeTarget={() => setShowTarget(false)} setReturnTarget={setReturnTarget} />
        </ReactModal>
    )
}

export default RerunSurveyModal
